import { Minus, Plus } from 'lucide-react'
import type { FieldValues, Path, Control } from 'react-hook-form'

import { Button } from 'src/shared/components/ui/buttons/button'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from 'src/shared/components/ui/forms/form'
import { cn } from 'src/shared/utils/cn'

interface CountFormFieldProps<T extends FieldValues> {
  name: Path<T>
  label: string
  description?: string
  className?: string
  fieldClassName?: string
  disabled?: boolean
  required?: boolean
  control?: Control<T>
  min?: number
  max?: number
  increment?: number
}

export function CountFormField<T extends FieldValues>({
  name,
  label,
  description,
  className,
  fieldClassName,
  disabled,
  required,
  control,
  min = 0,
  max = 25,
  increment = 1,
}: CountFormFieldProps<T>) {
  if (required) {
    label = `${label} *`
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('w-full', className)}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div
              className={cn(
                'flex w-full items-center justify-center rounded-md border bg-accent p-2',
                disabled && 'opacity-50',
                fieldClassName,
              )}
            >
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-6"
                onClick={() => {
                  const newValue = Math.max(min, (field.value || 0) - increment)
                  field.onChange(newValue)
                }}
                disabled={disabled || field.value <= min}
              >
                <Minus className="size-4" />
              </Button>
              <div className="flex h-10 w-12 items-center justify-center text-center">
                {Number(field.value || 0).toFixed(
                  increment % 1 === 0 || (field.value || 0) % 1 === 0 ? 0 : String(increment).split('.')[1].length,
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-6"
                onClick={() => {
                  const newValue = Math.min(max, (field.value || 0) + increment)
                  field.onChange(newValue)
                }}
                disabled={disabled || field.value >= max}
              >
                <Plus className="size-4" />
              </Button>
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
